"use client";

import { useEffect, useState } from "react";

// Bump when categories or wording change in a way that should re-prompt visitors.
// Note: smart_electric_support used "consent:v1" — we use the same key so that
// previous visitors who already consented on any Dux site are re-prompted once
// (key is the same, version field guards the schema).
export const CONSENT_VERSION = 1;
export const CONSENT_KEY = "consent:v1";
export const CONSENT_EVENT = "dux:consent:change";
export const CONSENT_OPEN_EVENT = "dux:consent:open";

export interface ConsentRecord {
  necessary: true;
  analytics: boolean;
  timestamp: number;
  version: number;
}

export const DEFAULT_CONSENT: ConsentRecord = {
  necessary: true,
  analytics: false,
  timestamp: 0,
  version: CONSENT_VERSION,
};

export function loadConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      parsed.necessary === true &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.timestamp === "number" &&
      parsed.version === CONSENT_VERSION
    ) {
      return parsed as ConsentRecord;
    }
  } catch {
    // Corrupted storage — treat as no consent.
  }
  return null;
}

export function saveConsent(analytics: boolean): ConsentRecord {
  const record: ConsentRecord = {
    necessary: true,
    analytics,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {}
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record }));
    document.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record }));
  }
  return record;
}

export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {}
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
  }
}

export function openConsentSettings() {
  if (typeof window === "undefined") return;
  document.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

/** React hook — subscribes to consent changes; returns null before hydration. */
export function useConsent(): ConsentRecord | null {
  const [consent, setConsent] = useState<ConsentRecord | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Read localStorage after hydration — useEffect is the correct place to avoid
    // SSR/client mismatch. eslint-disable required by react-hooks v5 rule.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConsent(loadConsent());
    setHydrated(true);
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<ConsentRecord | null>).detail;
      setConsent(detail ?? null);
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  return hydrated ? consent : null;
}
